(function () {
  const rails = new WeakSet();
  const compactQuery = window.matchMedia('(max-width: 991px)');

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
  const schedule = window.requestAnimationFrame.bind(window);

  const getAxis = () => (compactQuery.matches ? 'horizontal' : 'vertical');

  const getScrollStep = (rail) => {
    const axis = getAxis();
    const slides = Array.from(rail.querySelectorAll('.xo-product-info-media-carousel__thumb-slide'));
    const firstSlide = slides[0];
    const secondSlide = slides[1];

    if (!firstSlide) return 76;

    if (secondSlide) {
      const firstRect = firstSlide.getBoundingClientRect();
      const secondRect = secondSlide.getBoundingClientRect();
      const measuredGap = axis === 'horizontal' ? secondRect.left - firstRect.right : secondRect.top - firstRect.bottom;

      if (measuredGap >= 0) {
        return (axis === 'horizontal' ? firstRect.width : firstRect.height) + measuredGap;
      }
    }

    const thumbnail = rail.querySelector('.xo-product-info-media-carousel__thumbnail');
    const gap = Number.parseFloat(getComputedStyle(thumbnail).getPropertyValue('--xo-gap')) || 12;
    const firstRect = firstSlide.getBoundingClientRect();
    return (axis === 'horizontal' ? firstRect.width : firstRect.height) + gap;
  };

  const getScrollTarget = (rail) => rail.querySelector('.xo-product-info-media-carousel__thumbnail xo-carousel-inner');

  const getViewport = (rail) => rail.querySelector('[data-humm-thumb-viewport]') || getScrollTarget(rail);

  const getListBounds = (rail) => {
    const thumbnail = rail.querySelector('.xo-product-info-media-carousel__thumbnail');
    const slideCount = rail.querySelectorAll('.xo-product-info-media-carousel__thumb-slide').length;
    const perView = Number.parseFloat(getComputedStyle(thumbnail).getPropertyValue('--xo-per-view')) || slideCount;
    const step = getScrollStep(rail);
    const hiddenSlides = Math.max(slideCount - perView, 0);

    return {
      minTranslate: -(hiddenSlides * step),
      maxTranslate: 0,
      step,
    };
  };

  const getVisibleRange = (target, slides) => {
    const axis = getAxis();
    const viewportRect = target.getBoundingClientRect();
    let firstVisibleIndex = -1;
    let lastVisibleIndex = -1;

    slides.forEach((slide, index) => {
      const slideRect = slide.getBoundingClientRect();
      const isVisible =
        axis === 'horizontal'
          ? slideRect.right > viewportRect.left + 1 && slideRect.left < viewportRect.right - 1
          : slideRect.bottom > viewportRect.top + 1 && slideRect.top < viewportRect.bottom - 1;

      if (!isVisible) return;

      if (firstVisibleIndex === -1) firstVisibleIndex = index;
      lastVisibleIndex = index;
    });

    return { firstVisibleIndex, lastVisibleIndex };
  };

  const isSlideFullyVisible = (viewport, slide, axis) => {
    const viewportRect = viewport.getBoundingClientRect();
    const slideRect = slide.getBoundingClientRect();

    return axis === 'horizontal'
      ? slideRect.left >= viewportRect.left - 1 && slideRect.right <= viewportRect.right + 1
      : slideRect.top >= viewportRect.top - 1 && slideRect.bottom <= viewportRect.bottom + 1;
  };

  const allSlidesFitInViewport = (rail) => {
    const viewport = getViewport(rail);
    const target = getScrollTarget(rail);

    if (!viewport || !target) return true;

    const axis = getAxis();

    if (axis === 'vertical') {
      return target.scrollHeight <= viewport.clientHeight + 1;
    }

    return target.scrollWidth <= viewport.clientWidth + 1;
  };

  const resetRailScroll = (rail) => {
    const target = getScrollTarget(rail);
    const listEl = rail.querySelector('.xo-product-info-media-carousel__thumbnail xo-carousel-list');

    if (!target || !listEl) return;

    const axis = getAxis();
    const scrollPosition = axis === 'horizontal' ? target.scrollLeft : target.scrollTop;
    const translate = getListTranslate(listEl, axis);

    if (Math.abs(translate) > 0.5) {
      listEl.style.transition = 'none';
      listEl.style.transform = 'translate3d(0, 0, 0)';
      void listEl.offsetHeight;
      listEl.style.transition = '';
    }

    if (scrollPosition > 0) {
      target.scrollTo({
        [axis === 'horizontal' ? 'left' : 'top']: 0,
        behavior: 'auto',
      });
    }
  };

  const stabilizeRailIfAllVisible = (rail) => {
    if (!allSlidesFitInViewport(rail)) return false;

    resetRailScroll(rail);
    return true;
  };

  const setButtonVisibility = (button, visible) => {
    button.classList.toggle('is-visible', visible);
    button.disabled = !visible;
    button.setAttribute('aria-hidden', visible ? 'false' : 'true');
    button.tabIndex = visible ? 0 : -1;
  };

  const getListTranslate = (listEl, axis) => {
    if (!listEl) return 0;
    const matrix = new DOMMatrixReadOnly(getComputedStyle(listEl).transform);
    return axis === 'horizontal' ? matrix.m41 : matrix.m42;
  };

  const getEffectiveTranslate = (rail, listEl, axis, slides, viewport) => {
    const fromStyle = getListTranslate(listEl, axis);
    if (Math.abs(fromStyle) > 0.5) return fromStyle;

    const { firstVisibleIndex } = getVisibleRange(viewport, slides);
    if (firstVisibleIndex <= 0) return 0;

    return -firstVisibleIndex * getScrollStep(rail);
  };

  const applyListTranslate = (listEl, axis, translate) => {
    listEl.style.transition = 'transform 300ms ease';
    listEl.style.transform =
      axis === 'horizontal' ? `translate3d(${translate}px, 0, 0)` : `translate3d(0, ${translate}px, 0)`;
  };

  const updateButtons = (rail) => {
    const target = getScrollTarget(rail);
    const prevButton = rail.querySelector('[data-humm-thumb-scroll="prev"]');
    const nextButton = rail.querySelector('[data-humm-thumb-scroll="next"]');
    const slides = Array.from(rail.querySelectorAll('.xo-product-info-media-carousel__thumb-slide'));
    const listEl = rail.querySelector('.xo-product-info-media-carousel__thumbnail xo-carousel-list');

    if (!target || !prevButton || !nextButton) return;

    const axis = getAxis();
    const viewport = getViewport(rail);
    const scrollPosition = axis === 'horizontal' ? target.scrollLeft : target.scrollTop;
    const maxScroll = Math.max((axis === 'horizontal' ? target.scrollWidth - target.clientWidth : target.scrollHeight - target.clientHeight), 0);
    const effectiveTranslate = getEffectiveTranslate(rail, listEl, axis, slides, viewport);
    const { minTranslate } = getListBounds(rail);
    const allFit = allSlidesFitInViewport(rail);
    const { firstVisibleIndex, lastVisibleIndex } = getVisibleRange(viewport, slides);
    const hasIndexOverflow = firstVisibleIndex > 0 || (lastVisibleIndex !== -1 && lastVisibleIndex < slides.length - 1);
    const hasOverflow =
      !allFit && (maxScroll > 1 || hasIndexOverflow || effectiveTranslate < minTranslate + 1);
    const canScrollPrev = hasOverflow && (scrollPosition > 1 || effectiveTranslate < -1 || firstVisibleIndex > 0);
    const canScrollNext =
      hasOverflow &&
      (scrollPosition < maxScroll - 1 || effectiveTranslate > minTranslate + 1 || (lastVisibleIndex !== -1 && lastVisibleIndex < slides.length - 1));

    rail.dataset.hummThumbLayout = axis;
    rail.dataset.hummThumbScrollable = String(hasOverflow);
    rail.dataset.hummThumbOverflow = String(hasOverflow);
    setButtonVisibility(prevButton, canScrollPrev);
    setButtonVisibility(nextButton, canScrollNext);
  };

  const scheduleButtonUpdate = (rail, target) => {
    schedule(() => updateButtons(rail));

    if ('onscrollend' in target) return;

    window.setTimeout(() => updateButtons(rail), 350);
  };

  const scrollRail = (rail, direction) => {
    const target = getScrollTarget(rail);
    const listEl = rail.querySelector('.xo-product-info-media-carousel__thumbnail xo-carousel-list');
    if (!target || !listEl) return;

    const axis = getAxis();
    const viewport = getViewport(rail);
    const slides = Array.from(rail.querySelectorAll('.xo-product-info-media-carousel__thumb-slide'));
    const scrollPosition = axis === 'horizontal' ? target.scrollLeft : target.scrollTop;
    const step = getScrollStep(rail);
    const scrollOffset = getListTranslate(listEl, axis);
    const { minTranslate, maxTranslate } = getListBounds(rail);
    const { firstVisibleIndex, lastVisibleIndex } = getVisibleRange(viewport, slides);
    const nextTranslate = clamp(scrollOffset - direction * step, minTranslate, maxTranslate);

    if (nextTranslate !== scrollOffset) {
      applyListTranslate(listEl, axis, nextTranslate);
      if (scrollPosition > 0) {
        target.scrollTo({
          [axis === 'horizontal' ? 'left' : 'top']: 0,
          behavior: 'auto',
        });
      }
    } else if (direction < 0 && firstVisibleIndex > 0) {
      const targetSlide = slides[firstVisibleIndex - 1];

      if (targetSlide) {
        targetSlide.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
      }
    } else if (direction > 0 && lastVisibleIndex >= 0 && lastVisibleIndex < slides.length - 1) {
      const targetSlide = slides[lastVisibleIndex + 1];

      if (targetSlide) {
        targetSlide.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
      }
    }

    scheduleButtonUpdate(rail, target);
  };

  const syncActiveThumbnail = (rail) => {
    const target = getScrollTarget(rail);
    const activeSlide = rail.querySelector('.xo-product-info-media-carousel__thumb-slide[xo-active]:not([xo-active="false"])');

    if (!target || !activeSlide) {
      updateButtons(rail);
      return;
    }

    if (stabilizeRailIfAllVisible(rail)) {
      scheduleButtonUpdate(rail, target);
      return;
    }

    const viewport = getViewport(rail);
    const axis = getAxis();

    if (!isSlideFullyVisible(viewport, activeSlide, axis)) {
      activeSlide.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }

    scheduleButtonUpdate(rail, target);
  };

  const scheduleStabilizeIfAllVisible = (rail) => {
    schedule(() => {
      if (stabilizeRailIfAllVisible(rail)) updateButtons(rail);
      schedule(() => {
        if (stabilizeRailIfAllVisible(rail)) updateButtons(rail);
      });
    });
  };

  const initRail = (rail) => {
    if (rails.has(rail)) {
      updateButtons(rail);
      scheduleStabilizeIfAllVisible(rail);
      return;
    }

    const target = getScrollTarget(rail);
    const prevButton = rail.querySelector('[data-humm-thumb-scroll="prev"]');
    const nextButton = rail.querySelector('[data-humm-thumb-scroll="next"]');

    if (!target || !prevButton || !nextButton) return;

    rails.add(rail);
    prevButton.addEventListener('click', () => scrollRail(rail, -1));
    nextButton.addEventListener('click', () => scrollRail(rail, 1));
    target.addEventListener('scroll', () => updateButtons(rail), { passive: true });
    target.addEventListener('scrollend', () => updateButtons(rail), { passive: true });
    rail.addEventListener('click', (event) => {
      if (!event.target.closest('.xo-product-info-media-carousel__thumb-slide')) return;
      schedule(() => {
        syncActiveThumbnail(rail);
        scheduleStabilizeIfAllVisible(rail);
      });
    });
    updateButtons(rail);
    scheduleStabilizeIfAllVisible(rail);
  };

  const initRails = () => {
    document.querySelectorAll('[data-humm-thumbnail-rail]').forEach(initRail);
  };

  initRails();
  window.addEventListener('resize', initRails);
  compactQuery.addEventListener('change', initRails);

  const observer = new MutationObserver(initRails);
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['style', 'xo-active', 'xo-visible'],
    childList: true,
    subtree: true,
  });

  /* -------------------------------------------------------------------------
     PDP variant → featured media sync (HUMM main gallery)
     ------------------------------------------------------------------------- */

  const PDP_PRODUCT_SELECTOR = 'xo-product[xo-product-information]';
  const PDP_GALLERY_SELECTOR = '[data-humm-variant-media-sync]';
  const QUICK_VIEW_SELECTOR = 'xo-product-quick-view';

  const getPdpProduct = (el) => el.closest(PDP_PRODUCT_SELECTOR);

  const isPdpVariantInput = (input) => {
    if (!(input instanceof HTMLInputElement)) return false;
    if (!input.closest('xo-product-variants')) return false;
    const product = getPdpProduct(input);
    if (!product) return false;
    if (input.closest(QUICK_VIEW_SELECTOR)) return false;
    return true;
  };

  const getPdpGallery = (product) => product?.querySelector(PDP_GALLERY_SELECTOR) || null;

  const getVariantMap = (gallery) => {
    const mapEl = gallery.querySelector('.xo-product-info-media-carousel__variant-map');
    if (!mapEl) return null;

    try {
      return JSON.parse(mapEl.textContent);
    } catch (_e) {
      return null;
    }
  };

  const getMediaIdForVariant = (gallery, product, variantId) => {
    if (!variantId) return null;

    const map = getVariantMap(gallery);
    if (map && map[variantId]) {
      return String(map[variantId]);
    }

    const dataEl = product?.querySelector('xo-product-data');
    if (!dataEl) return null;

    const template = dataEl.querySelector('template');
    const raw = (template?.content?.textContent || dataEl.textContent || '').trim();
    if (!raw) return null;

    try {
      const data = JSON.parse(raw);
      const variant = (data.variants || []).find((v) => String(v.id) === String(variantId));
      if (!variant) return null;

      if (variant.featured_media?.id) {
        return String(variant.featured_media.id);
      }
      if (variant.featured_image?.id) {
        const mediaMatch = (data.media || []).find(
          (m) =>
            String(m.id) === String(variant.featured_image.id) ||
            (variant.featured_image.src && m.preview_image?.src === variant.featured_image.src)
        );
        if (mediaMatch?.id) {
          return String(mediaMatch.id);
        }
        return String(variant.featured_image.id);
      }
    } catch (_e) {
      return null;
    }

    return null;
  };

  const getVariantMediaId = (input, gallery) => {
    const fromInput = input.getAttribute('data-featured-media-id');
    if (fromInput) return String(fromInput);

    const variantId = input.getAttribute('xo-variant-id');
    if (!variantId) return null;

    const map = getVariantMap(gallery);
    if (map && map[variantId]) {
      return String(map[variantId]);
    }

    const product = getPdpProduct(input);
    const dataEl = product?.querySelector('xo-product-data');
    if (!dataEl) return null;

    const template = dataEl.querySelector('template');
    const raw = (template?.content?.textContent || dataEl.textContent || '').trim();
    if (!raw) return null;

    try {
      const data = JSON.parse(raw);
      const variant = (data.variants || []).find((v) => String(v.id) === String(variantId));
      if (!variant) return null;

      if (variant.featured_media?.id) {
        return String(variant.featured_media.id);
      }
      if (variant.featured_image?.id) {
        const mediaMatch = (data.media || []).find(
          (m) =>
            String(m.id) === String(variant.featured_image.id) ||
            (variant.featured_image.src && m.preview_image?.src === variant.featured_image.src)
        );
        if (mediaMatch?.id) {
          return String(mediaMatch.id);
        }
        return String(variant.featured_image.id);
      }
    } catch (_e) {
      return null;
    }

    return null;
  };

  const getMainSlides = (gallery) => {
    const carousel = gallery.querySelector('xo-carousel');
    if (!carousel) return [];

    const mainInner = carousel.querySelector(':scope > xo-carousel-inner');
    if (!mainInner) return [];

    return Array.from(mainInner.querySelectorAll('xo-carousel-slide[data-media-id]'));
  };

  const getCarouselName = (gallery) => {
    const carousel = gallery.querySelector('xo-carousel');
    if (!carousel) return null;

    return carousel.xoName || carousel.getAttribute('xo-name');
  };

  const syncRailToActiveThumb = (gallery) => {
    const rail = gallery.querySelector('[data-humm-thumbnail-rail]');
    if (!rail) return;

    schedule(() => {
      syncActiveThumbnail(rail);
      scheduleStabilizeIfAllVisible(rail);
    });
  };

  const goToMediaSlide = (gallery, mediaId) => {
    if (!mediaId) return false;

    const mainSlides = getMainSlides(gallery);
    const index = mainSlides.findIndex(
      (slide) => String(slide.getAttribute('data-media-id')) === String(mediaId)
    );

    if (index < 0) return false;

    const carouselName = getCarouselName(gallery);
    if (carouselName && window.xoCarousel?.goTo) {
      window.xoCarousel.goTo(carouselName, index);
      syncRailToActiveThumb(gallery);
      return true;
    }

    const thumb = gallery.querySelector(
      `.xo-product-info-media-carousel__thumb-slide[data-media-id="${CSS.escape(String(mediaId))}"]`
    );
    if (thumb) {
      thumb.click();
      syncRailToActiveThumb(gallery);
      return true;
    }

    return false;
  };

  const syncGalleryToProduct = (product, fallbackInput) => {
    if (!product) return;

    const gallery = getPdpGallery(product);
    if (!gallery) return;

    const selectedVariantId = product.getAttribute('xo-selected-or-first-available-variant-id');
    let mediaId = getMediaIdForVariant(gallery, product, selectedVariantId);

    if (!mediaId && fallbackInput) {
      mediaId = getVariantMediaId(fallbackInput, gallery);
    }

    if (!mediaId) return;

    goToMediaSlide(gallery, mediaId);
  };

  const syncGalleryToVariant = (input) => {
    if (!input) return;
    syncGalleryToProduct(getPdpProduct(input), input);
  };

  const scheduleVariantSync = (input) => {
    syncGalleryToVariant(input);
    schedule(() => syncGalleryToVariant(input));
    window.setTimeout(() => syncGalleryToVariant(input), 80);
    window.setTimeout(() => syncGalleryToVariant(input), 400);
  };

  const scheduleVariantSyncForProduct = (product) => {
    const fallbackInput = product.querySelector('xo-product-variants input[type="radio"]:checked');
    syncGalleryToProduct(product, fallbackInput);
    schedule(() => syncGalleryToProduct(product, fallbackInput));
    window.setTimeout(() => syncGalleryToProduct(product, fallbackInput), 80);
    window.setTimeout(() => syncGalleryToProduct(product, fallbackInput), 400);
  };

  const observePdpVariantChanges = () => {
    document.querySelectorAll(PDP_PRODUCT_SELECTOR).forEach((product) => {
      if (!getPdpGallery(product) || product.dataset.hummVariantMediaObserved === 'true') return;

      product.dataset.hummVariantMediaObserved = 'true';

      const variantObserver = new MutationObserver(() => {
        scheduleVariantSyncForProduct(product);
      });

      variantObserver.observe(product, {
        attributes: true,
        attributeFilter: ['xo-selected-or-first-available-variant-id'],
      });
    });
  };

  observePdpVariantChanges();

  const pdpGalleryObserver = new MutationObserver(() => {
    observePdpVariantChanges();
  });

  pdpGalleryObserver.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });

  const resolveVariantInput = (target) => {
    if (target instanceof HTMLInputElement && target.type === 'radio') {
      return target;
    }

    const field = target.closest?.('.xo-field-color');
    if (field) {
      return field.querySelector('input[type="radio"]');
    }

    return null;
  };

  const onPdpVariantInteraction = (event) => {
    const input = resolveVariantInput(event.target);
    if (!input || !isPdpVariantInput(input)) return;

    scheduleVariantSync(input);
  };

  document.addEventListener('change', onPdpVariantInteraction, true);
  document.addEventListener('input', onPdpVariantInteraction, true);

  document.addEventListener(
    'click',
    (event) => {
      const field = event.target.closest?.('.xo-field-color--pill, .xo-field-color');
      if (!field) return;

      const input = field.querySelector('input[type="radio"]');
      if (!input || !isPdpVariantInput(input)) return;

      scheduleVariantSync(input);
    },
    true
  );
})();
